import { brandColors } from '@/lib/brandColors'

export const PROMOTION_BANNER_THEMES = ['turquoise', 'magenta', 'yellow', 'blue'] as const

export type PromotionBannerTheme = (typeof PROMOTION_BANNER_THEMES)[number]

export const PROMOTION_BANNER_THEME_COLORS: Record<PromotionBannerTheme, string> = {
  turquoise: brandColors.turquoise,
  magenta: brandColors.pink,
  yellow: brandColors.tangerine,
  blue: brandColors.blue,
}

export const PROMOTION_BANNER_THEME_OPTIONS = [
  { label: 'Turquoise', value: 'turquoise' },
  { label: 'Magenta', value: 'magenta' },
  { label: 'Yellow', value: 'yellow' },
  { label: 'Blue', value: 'blue' },
] as const satisfies ReadonlyArray<{ label: string; value: PromotionBannerTheme }>

export type PromotionBannerThemeClasses = {
  backgroundClassName: string
  focusRingClassName: string
  headlineClassName: string
  navButtonClassName: string
  ctaClassName: string
}

const sharedTextClasses = 'text-bluetext'
const sharedNavButtonClasses =
  'border border-bluetext/80 text-bluetext transition-colors hover:bg-bluetext/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50'
const sharedCtaClasses =
  'border border-bluetext bg-transparent text-bluetext transition-colors hover:bg-bluetext/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50'

export const PROMOTION_BANNER_THEME_CLASSES: Record<PromotionBannerTheme, PromotionBannerThemeClasses> =
  {
    turquoise: {
      backgroundClassName: 'bg-turquoise',
      focusRingClassName: 'focus-visible:ring-turquoise/50',
      headlineClassName: sharedTextClasses,
      navButtonClassName: sharedNavButtonClasses,
      ctaClassName: sharedCtaClasses,
    },
    magenta: {
      backgroundClassName: 'bg-brand-pink',
      focusRingClassName: 'focus-visible:ring-brand-pink/50',
      headlineClassName: sharedTextClasses,
      navButtonClassName: sharedNavButtonClasses,
      ctaClassName: sharedCtaClasses,
    },
    yellow: {
      backgroundClassName: 'bg-tangerine',
      focusRingClassName: 'focus-visible:ring-tangerine/50',
      headlineClassName: sharedTextClasses,
      navButtonClassName: sharedNavButtonClasses,
      ctaClassName: sharedCtaClasses,
    },
    blue: {
      backgroundClassName: 'bg-brand-blue',
      focusRingClassName: 'focus-visible:ring-brand-blue/50',
      headlineClassName: sharedTextClasses,
      navButtonClassName: sharedNavButtonClasses,
      ctaClassName: sharedCtaClasses,
    },
  }

export function getPromotionBannerThemeColor(
  theme: string | null | undefined,
): string | null {
  if (theme && PROMOTION_BANNER_THEMES.includes(theme as PromotionBannerTheme)) {
    return PROMOTION_BANNER_THEME_COLORS[theme as PromotionBannerTheme]
  }

  return null
}

export function resolvePromotionBannerTheme(
  theme: string | null | undefined,
  fallback: PromotionBannerTheme = 'turquoise',
): PromotionBannerTheme {
  if (theme && PROMOTION_BANNER_THEMES.includes(theme as PromotionBannerTheme)) {
    return theme as PromotionBannerTheme
  }

  return fallback
}

export function getPromotionBannerThemeClasses(theme: PromotionBannerTheme): PromotionBannerThemeClasses {
  return PROMOTION_BANNER_THEME_CLASSES[theme]
}
